import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  LinearProgress,
  Collapse,
  Button,
  Tabs,
  Tab,
  Divider,
  Stack,
  Checkbox,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Toolbar,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import * as XLSX from 'xlsx'
import { useUserStore } from '../../stores/userStore'
import { useSurveyStore } from '../../stores/surveyStore'
import { useCareerCategoryStore } from '../../stores/careerCategoryStore'
import {
  Survey,
  SURVEY_TYPE_LABELS,
  SURVEY_DIMENSION_LABELS,
  SURVEY_MAX_SCORES,
} from '../../models/Survey'
import {
  SURVEY_QUESTIONS,
  SURVEY_OPTION_LABELS,
} from '../../models/SurveyQuestions'
import { calculateCareerCategory } from '../../utils/careerCategoryEngine'

const SURVEY_TYPE_COLORS: Record<string, string> = {
  career_calling: '#2196F3',
  outcome_expectation: '#4CAF50',
  bfi15: '#FF9800',
}

function exportToExcel(surveys: Survey[]) {
  const wb = XLSX.utils.book_new()
  const dateStr = format(new Date(), 'yyyyMMdd')

  // Sheet 1: 量表總覽（所有類型，分數）
  // 預先展開所有量表類型的構面欄位，保持固定欄位順序
  const allDimHeaders: Array<{ key: string; surveyType: string; header: string }> = []
  for (const [sType, dims] of Object.entries(SURVEY_DIMENSION_LABELS)) {
    Object.keys(dims).forEach((key, i) => {
      allDimHeaders.push({ key, surveyType: sType, header: `${dims[key]}（${i + 1}）` })
    })
  }

  const summaryRows = surveys.map((s) => {
    const dimLabels = SURVEY_DIMENSION_LABELS[s.surveyType] ?? {}
    const dimKeys = Object.keys(dimLabels)
    const dominantIdx = dimKeys.indexOf(s.dominantDimension)
    const dominantLabel = dimLabels[s.dominantDimension] ?? s.dominantDimension
    const dominantWithNum = dominantIdx >= 0 ? `${dominantLabel}（${dominantIdx + 1}）` : dominantLabel

    const isOE = s.surveyType === 'outcome_expectation'
    const oeSum = isOE
      ? Object.values(s.scores).reduce((acc, v) => acc + v, 0)
      : null

    const row: Record<string, string | number> = {
      用戶姓名: s.userName ?? s.userId,
      量表類型: SURVEY_TYPE_LABELS[s.surveyType] ?? s.surveyType,
      完成時間: format(s.completedAt, 'yyyy-MM-dd HH:mm'),
      開始時間: s.startedAt ? format(s.startedAt, 'yyyy-MM-dd HH:mm') : '',
      填寫時長: formatDurationSec(s.durationSeconds),
      '暫停次數': s.pauseCount ?? '',
      是否重填: s.isRetake != null ? (s.isRetake ? '是' : '否') : '',
      入口來源: s.entrySource ?? '',
      主要特質: dominantWithNum,
      '結果預期總分（/40）': oeSum !== null ? oeSum : '',
      高低結果預期: oeSum !== null ? (oeSum >= 25 ? '高' : '低') : '',
    }
    for (const { key, surveyType, header } of allDimHeaders) {
      row[header] =
        surveyType === s.surveyType && s.scores[key] !== undefined
          ? Number(s.scores[key].toFixed(2))
          : ''
    }
    return row
  })
  const summaryHeader = [
    '用戶姓名', '量表類型', '完成時間', '開始時間', '填寫時長', '暫停次數',
    '是否重填', '入口來源', '主要特質',
    '結果預期總分（/40）', '高低結果預期',
    ...allDimHeaders.map((h) => h.header),
  ]
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(summaryRows, { header: summaryHeader }),
    '量表總覽'
  )

  // Sheet 2–4: 各量表逐題作答
  const TYPE_SHEET_NAMES: Record<string, string> = {
    career_calling: '生涯召喚',
    outcome_expectation: '結果預期',
    bfi15: '人格特質',
  }

  for (const [surveyType, sheetName] of Object.entries(TYPE_SHEET_NAMES)) {
    const typeSurveys = surveys.filter((s) => s.surveyType === surveyType)
    if (typeSurveys.length === 0) continue

    const questions = SURVEY_QUESTIONS[surveyType] ?? []
    const optionLabels = SURVEY_OPTION_LABELS[surveyType] ?? []
    const dimLabels = SURVEY_DIMENSION_LABELS[surveyType] ?? {}

    const rows = typeSurveys.map((s) => {
      // 逐題作答
      const answerColumns: Record<string, string> = {}
      questions.forEach((q) => {
        const header = `Q${q.number}: ${q.text}`
        const ansNum = s.answers[String(q.number)]
        answerColumns[header] =
          ansNum !== undefined && ansNum >= 1 && ansNum <= optionLabels.length
            ? optionLabels[ansNum - 1]
            : ''
      })

      return {
        用戶姓名: s.userName ?? s.userId,
        完成時間: format(s.completedAt, 'yyyy-MM-dd HH:mm'),
        主要特質: dimLabels[s.dominantDimension] ?? s.dominantDimension,
        ...answerColumns,
      }
    })

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), sheetName)
  }

  XLSX.writeFile(wb, `survey_export_${dateStr}.xlsx`)
}

function formatDurationSec(seconds?: number): string {
  if (seconds == null) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function DimensionChips({ survey }: { survey: Survey }) {
  const dimLabels = SURVEY_DIMENSION_LABELS[survey.surveyType] ?? {}
  const sorted = Object.entries(survey.scores).sort(([, a], [, b]) => b - a)
  const top2 = sorted.slice(0, 2)

  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {top2.map(([key, score]) => (
        <Chip
          key={key}
          label={`${dimLabels[key] ?? key}: ${score.toFixed(1)}`}
          size="small"
          variant="outlined"
        />
      ))}
    </Box>
  )
}

function DimensionProgressBars({ survey }: { survey: Survey }) {
  const dimLabels = SURVEY_DIMENSION_LABELS[survey.surveyType] ?? {}
  const maxScore = SURVEY_MAX_SCORES[survey.surveyType] ?? 1

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Grid container spacing={2}>
        {Object.keys(dimLabels).map((key) => {
          const score = survey.scores[key] ?? 0
          const pct = Math.min((score / maxScore) * 100, 100)
          return (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">
                  {dimLabels[key] ?? key}
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {score.toFixed(1)} / {maxScore}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

/** 顯示單一量表的逐題作答細節，依構面分組 */
function SurveyAnswerDetail({ survey }: { survey: Survey }) {
  const dimLabels = SURVEY_DIMENSION_LABELS[survey.surveyType] ?? {}
  const questions = SURVEY_QUESTIONS[survey.surveyType] ?? []
  const optionLabels = SURVEY_OPTION_LABELS[survey.surveyType] ?? []

  // 依 dimension 分組（保留 questions 原本的順序作為次序基準）
  const grouped = new Map<string, typeof questions>()
  for (const q of questions) {
    if (!grouped.has(q.dimensionKey)) grouped.set(q.dimensionKey, [])
    grouped.get(q.dimensionKey)!.push(q)
  }

  if (questions.length === 0) {
    return (
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          無題目資料。
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Stack spacing={2.5}>
        {Array.from(grouped.entries()).map(([dimKey, qs]) => (
          <Box key={dimKey}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}
            >
              {dimLabels[dimKey] ?? dimKey}
            </Typography>
            <Stack spacing={0.75} sx={{ pl: 1 }}>
              {qs.map((q) => {
                const ansNum = survey.answers[String(q.number)]
                const ansLabel =
                  ansNum !== undefined && ansNum >= 1 && ansNum <= optionLabels.length
                    ? optionLabels[ansNum - 1]
                    : '未作答'
                return (
                  <Box
                    key={q.number}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'flex-start',
                      py: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 24,
                        color: 'text.secondary',
                        fontFamily: 'monospace',
                      }}
                    >
                      {q.number}.
                    </Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {q.text}
                    </Typography>
                    <Chip
                      label={ansLabel}
                      size="small"
                      color={ansNum !== undefined ? 'primary' : 'default'}
                      variant={ansNum !== undefined ? 'filled' : 'outlined'}
                      sx={{ minWidth: 80 }}
                    />
                  </Box>
                )
              })}
            </Stack>
            <Divider sx={{ mt: 1.5 }} />
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

/** 展開行內的小 Tab：分數概覽 / 作答細節 */
function ExpandedSurveyDetail({ survey }: { survey: Survey }) {
  const [innerTab, setInnerTab] = useState(0)
  return (
    <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
      <Tabs
        value={innerTab}
        onChange={(_, v) => setInnerTab(v)}
        sx={{ px: 2, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
      >
        <Tab label="分數概覽" />
        <Tab label="作答細節" />
      </Tabs>
      {innerTab === 0 && <DimensionProgressBars survey={survey} />}
      {innerTab === 1 && <SurveyAnswerDetail survey={survey} />}
    </Box>
  )
}

interface OverviewRow {
  userId: string
  userName: string
  career_calling?: Survey
  outcome_expectation?: Survey
  bfi15?: Survey
}

const LS_KEY = 'nursebridge_survey_delete_enabled'

export default function SurveyListPage() {
  const { users, fetchUsers } = useUserStore()
  const {
    allSurveys,
    loading,
    error,
    fetchAllSurveys,
    deleteSurvey,
    deleteSurveysBatch,
  } = useSurveyStore()
  const { categories, fetchCategories } = useCareerCategoryStore()

  const [canDelete, setCanDelete] = useState(
    () => localStorage.getItem(LS_KEY) === 'true'
  )

  const toggleDelete = (enabled: boolean) => {
    localStorage.setItem(LS_KEY, String(enabled))
    setCanDelete(enabled)
  }

  const [topTab, setTopTab] = useState(0)
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [userFilter, setUserFilter] = useState<{ id: string; label: string } | null>(null)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [deleteTarget, setDeleteTarget] = useState<string[] | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    if (users.length > 0) {
      fetchAllSurveys(users.map((u) => ({ id: u.id, displayName: u.displayName })))
    }
  }, [users.length])

  useEffect(() => {
    if (categories.length === 0) {
      fetchCategories()
    }
  }, [categories.length, fetchCategories])

  // 有填過量表的用戶清單（供下拉選單用）
  const userOptions = useMemo(() => {
    const seen = new Map<string, string>()
    allSurveys.forEach((s) => {
      if (!seen.has(s.userId)) seen.set(s.userId, s.userName ?? s.userId)
    })
    return Array.from(seen.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'zh-Hant'))
  }, [allSurveys])

  const filtered = useMemo(() => {
    return allSurveys.filter((s) => {
      if (userFilter && s.userId !== userFilter.id) return false
      if (typeFilter !== 'all' && s.surveyType !== typeFilter) return false
      if (startDate) {
        const start = new Date(startDate)
        start.setHours(0, 0, 0, 0)
        if (s.completedAt < start) return false
      }
      if (endDate) {
        const end = new Date(endDate)
        end.setHours(23, 59, 59, 999)
        if (s.completedAt > end) return false
      }
      return true
    })
  }, [allSurveys, userFilter, typeFilter, startDate, endDate])

  // Filter 變更時，重設分頁與選取
  useEffect(() => {
    setPage(0)
    setSelected(new Set())
  }, [userFilter, typeFilter, startDate, endDate])

  const pagedRows = useMemo(() => {
    if (rowsPerPage <= 0) return filtered
    const start = page * rowsPerPage
    return filtered.slice(start, start + rowsPerPage)
  }, [filtered, page, rowsPerPage])

  const countByType = (type: string) =>
    filtered.filter((s) => s.surveyType === type).length

  const handleRowClick = (id: string) => {
    setExpandedRow((prev) => (prev === id ? null : id))
  }

  const toggleSelectOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const togglePageSelection = () => {
    const pageIds = pagedRows.map((r) => r.id)
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        pageIds.forEach((id) => next.delete(id))
      } else {
        pageIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const selectAllFiltered = () => {
    setSelected(new Set(filtered.map((s) => s.id)))
  }

  const clearSelection = () => setSelected(new Set())

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      if (deleteTarget.length === 1) {
        await deleteSurvey(deleteTarget[0])
      } else {
        await deleteSurveysBatch(deleteTarget)
      }
      setSelected((prev) => {
        const next = new Set(prev)
        deleteTarget.forEach((id) => next.delete(id))
        return next
      })
      setDeleteTarget(null)
    } catch {
      // 錯誤訊息已由 store set 到 error，dialog 保持開啟讓使用者重試或取消
    } finally {
      setDeleting(false)
    }
  }

  // 職涯總覽：按 userId group + 取每型最新一份
  const overviewRows: OverviewRow[] = useMemo(() => {
    const userMap = new Map<string, Map<string, Survey>>()
    // allSurveys 已按 completedAt desc 排序，第一個就是最新
    allSurveys.forEach((s) => {
      if (!userMap.has(s.userId)) userMap.set(s.userId, new Map())
      const us = userMap.get(s.userId)!
      if (!us.has(s.surveyType)) us.set(s.surveyType, s)
    })
    const rows: OverviewRow[] = []
    userMap.forEach((typeMap, userId) => {
      const userName =
        typeMap.values().next().value?.userName ?? userId
      rows.push({
        userId,
        userName,
        career_calling: typeMap.get('career_calling'),
        outcome_expectation: typeMap.get('outcome_expectation'),
        bfi15: typeMap.get('bfi15'),
      })
    })
    // 依姓名排序
    rows.sort((a, b) => a.userName.localeCompare(b.userName, 'zh-Hant'))
    return rows
  }, [allSurveys])

  const renderTraitCell = (survey: Survey | undefined, type: string) => {
    if (!survey) return <Typography variant="body2" color="text.disabled">—</Typography>
    const dimLabels = SURVEY_DIMENSION_LABELS[type] ?? {}
    const trait = dimLabels[survey.dominantDimension] ?? survey.dominantDimension
    return (
      <Box>
        <Typography variant="body2" fontWeight={500}>
          {trait}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {format(survey.completedAt, 'yyyy/MM/dd', { locale: zhTW })}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          量表管理
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={canDelete}
                onChange={(e) => toggleDelete(e.target.checked)}
                color="error"
                size="small"
              />
            }
            label={
              <Typography variant="body2" color={canDelete ? 'error' : 'text.secondary'}>
                刪除模式
              </Typography>
            }
          />
          {topTab === 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => exportToExcel(filtered)}
              disabled={filtered.length === 0}
            >
              匯出 Excel
            </Button>
          )}
        </Box>
      </Box>

      <Tabs value={topTab} onChange={(_, v) => setTopTab(v)} sx={{ mb: 3 }}>
        <Tab label="量表列表" />
        <Tab label="職涯總覽" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tab 0: 量表列表 */}
      {topTab === 0 && (
        <>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={userOptions}
                value={userFilter}
                onChange={(_, v) => setUserFilter(v)}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label="篩選用戶" placeholder="輸入姓名搜尋" />
                )}
                noOptionsText="查無用戶"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>量表類型</InputLabel>
                <Select
                  value={typeFilter}
                  label="量表類型"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">全部</MenuItem>
                  {Object.entries(SURVEY_TYPE_LABELS).map(([key, label]) => (
                    <MenuItem key={key} value={key}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="開始日期"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="結束日期"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Summary cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {Object.entries(SURVEY_TYPE_LABELS).map(([type, label]) => (
              <Grid item xs={12} sm={4} key={type}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {label}
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {countByType(type)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          份完成
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: SURVEY_TYPE_COLORS[type] + '20',
                          border: `2px solid ${SURVEY_TYPE_COLORS[type]}`,
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper variant="outlined">
              {canDelete && selected.size > 0 && (
                <Toolbar
                  sx={{
                    pl: 2,
                    pr: 1,
                    bgcolor: 'primary.lighter',
                    borderBottom: 1,
                    borderColor: 'divider',
                    minHeight: 56,
                  }}
                >
                  <Typography sx={{ flex: 1 }} variant="subtitle1">
                    已選取 {selected.size} 筆
                  </Typography>
                  {selected.size < filtered.length && (
                    <Button size="small" onClick={selectAllFiltered} sx={{ mr: 1 }}>
                      全選（{filtered.length} 筆）
                    </Button>
                  )}
                  <Button size="small" onClick={clearSelection} sx={{ mr: 1 }}>
                    取消選取
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteTarget(Array.from(selected))}
                  >
                    批次刪除
                  </Button>
                </Toolbar>
              )}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {canDelete && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              pagedRows.some((r) => selected.has(r.id)) &&
                              !pagedRows.every((r) => selected.has(r.id))
                            }
                            checked={
                              pagedRows.length > 0 &&
                              pagedRows.every((r) => selected.has(r.id))
                            }
                            onChange={togglePageSelection}
                          />
                        </TableCell>
                      )}
                      <TableCell>用戶姓名</TableCell>
                      <TableCell>量表</TableCell>
                      <TableCell>完成時間</TableCell>
                      <TableCell>主要特質</TableCell>
                      <TableCell>各構面分數</TableCell>
                      <TableCell>填寫時長</TableCell>
                      <TableCell>重填</TableCell>
                      <TableCell>入口</TableCell>
                      {canDelete && <TableCell align="right">操作</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canDelete ? 10 : 8} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">暫無資料</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedRows.map((survey) => {
                        const dimLabels = SURVEY_DIMENSION_LABELS[survey.surveyType] ?? {}
                        const dominantLabel =
                          dimLabels[survey.dominantDimension] ?? survey.dominantDimension
                        const isExpanded = expandedRow === survey.id
                        const isSelected = selected.has(survey.id)

                        return (
                          <>
                            <TableRow
                              key={survey.id}
                              hover
                              selected={isSelected}
                              onClick={() => handleRowClick(survey.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              {canDelete && (
                                <TableCell
                                  padding="checkbox"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    onChange={() => toggleSelectOne(survey.id)}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                {survey.userName ?? survey.userId}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={SURVEY_TYPE_LABELS[survey.surveyType]}
                                  size="small"
                                  sx={{
                                    bgcolor: SURVEY_TYPE_COLORS[survey.surveyType] + '20',
                                    color: SURVEY_TYPE_COLORS[survey.surveyType],
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                {format(survey.completedAt, 'yyyy/MM/dd HH:mm', {
                                  locale: zhTW,
                                })}
                              </TableCell>
                              <TableCell>{dominantLabel}</TableCell>
                              <TableCell>
                                <DimensionChips survey={survey} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {formatDurationSec(survey.durationSeconds)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {survey.isRetake != null ? (
                                  <Chip
                                    label={survey.isRetake ? '重填' : '首次'}
                                    size="small"
                                    color={survey.isRetake ? 'warning' : 'success'}
                                    variant="outlined"
                                  />
                                ) : (
                                  <Typography variant="body2" color="text.disabled">—</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {survey.entrySource ?? '—'}
                                </Typography>
                              </TableCell>
                              {canDelete && (
                                <TableCell
                                  align="right"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Tooltip title="刪除這筆量表">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => setDeleteTarget([survey.id])}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                            <TableRow key={`${survey.id}-detail`}>
                              <TableCell
                                colSpan={canDelete ? 10 : 8}
                                sx={{ py: 0, border: isExpanded ? undefined : 'none' }}
                              >
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <ExpandedSurveyDetail survey={survey} />
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage > 0 ? rowsPerPage : filtered.length || 1}
                onRowsPerPageChange={(e) => {
                  const value = parseInt(e.target.value, 10)
                  setRowsPerPage(value)
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 25, 50, 100, { label: '全部', value: -1 }]}
                labelRowsPerPage="每頁顯示"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} / 共 ${count}`
                }
              />
            </Paper>
          )}
        </>
      )}

      {/* 刪除確認 Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
      >
        <DialogTitle>確認刪除量表</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget && deleteTarget.length === 1
              ? '確定要刪除此筆量表資料嗎？此操作無法復原。'
              : `確定要刪除選取的 ${deleteTarget?.length ?? 0} 筆量表資料嗎？此操作無法復原。`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            取消
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
            }
          >
            {deleting ? '刪除中…' : '確認刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tab 1: 職涯總覽 */}
      {topTab === 1 && (
        <>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>用戶姓名</TableCell>
                    <TableCell>生涯召喚</TableCell>
                    <TableCell>結果預期</TableCell>
                    <TableCell>人格特質</TableCell>
                    <TableCell>職涯分類</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overviewRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">暫無資料</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    overviewRows.map((row) => {
                      const result = calculateCareerCategory(
                        {
                          career_calling: row.career_calling,
                          outcome_expectation: row.outcome_expectation,
                          bfi15: row.bfi15,
                        },
                        categories
                      )
                      return (
                        <TableRow key={row.userId} hover>
                          <TableCell>{row.userName}</TableCell>
                          <TableCell>{renderTraitCell(row.career_calling, 'career_calling')}</TableCell>
                          <TableCell>{renderTraitCell(row.outcome_expectation, 'outcome_expectation')}</TableCell>
                          <TableCell>{renderTraitCell(row.bfi15, 'bfi15')}</TableCell>
                          <TableCell>
                            {result ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label={`#${result.matchedNumbers.join(', #')}`}
                                  color="primary"
                                  size="small"
                                />
                                <Typography variant="body2">
                                  {result.displayCategory.categoryLabel}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  )
}
